import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styles from './mega-nav-desktop.module.css';
import { NavigationMenu, PageReference } from '../../../typescript/response';
import { Chevron } from '../../icon/chevron';
import { CSLocale } from '../../../typescript/component';
import { LanguageSelector } from '../../ui';
import { useRouter } from 'next/router';
import { getCachedAsset } from '../../../helper/utils';

interface MegaNavDesktopProps {
  navMenu: NavigationMenu[];
  currentLocale: string;
  locations?: PageReference;
  request_quote: PageReference;
  contact_us?: PageReference;
  showLanguageSelector?: boolean;
  languages: CSLocale[] | null;
  onLocaleChange: (newLocale: string) => void;
  header_strings?: Record<string, string>;
}

export default function MegaNavDesktop({
  navMenu,
  currentLocale,
  showLanguageSelector,
  languages,
  onLocaleChange,
  locations,
  request_quote,
  contact_us,
  header_strings
}: MegaNavDesktopProps) {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [openMenuScrollable, setOpenMenuScrollable] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const location = `/${pathname?.split('/')[1] || ''}`;
  const { locale, defaultLocale } = useRouter();
  const isDefaultLocale = locale === defaultLocale;

  const toggleDropdown = (index: number) => {
    setOpenMenuIndex(prevIndex => (prevIndex === index ? null : index));
  };

  const getHash = (hash?: string) => (!hash ? '' : hash.startsWith('#') ? hash : `#${hash}`);

  const getDropdownBanner = (nav: NavigationMenu) => {
    const { row_image, column_image, label } = nav;
    if (row_image?.image) {
      const remainder = (nav?.sub_navigation?.length ?? 0) % 3;
      const blankItemsNeeded = remainder === 0 ? 2 : 2 - remainder;
      const itemMissing = Array.from({ length: blankItemsNeeded }, (_, index) => (
        <div key={`blank-${index}`} className={styles.megaMenuL2}></div>
      ));
      return (
        <>
          {itemMissing}
          <div key={`${label}-banner`}>
            <a href={row_image.url}>
              <img
                className={styles.bannerImage}
                src={getCachedAsset(row_image.image.url)}
                alt={row_image.image.title}
              />
            </a>
          </div>
        </>
      );
    }
    if (column_image?.image) {
      return (
        <div
          key={`${label}-banner`}
          className={styles.dropdownBannerCol}
          style={{ gridRow: `1 / span ${Math.ceil((nav?.sub_navigation?.length ?? 0) / 2)}` }}
        >
          <a href={column_image.url}>
            <img
              className={styles.bannerImage}
              src={getCachedAsset(column_image.image.url)}
              alt={column_image.image.title}
            />
          </a>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={styles.links} ref={dropdownRef}>
      {navMenu?.map((list, idx) => {
        const dropdownRef = useRef<HTMLDivElement | null>(null)
        var showParentLinkInDropdown: string | null = 'top';
        const parentLinkInDropdown = <Link
          className={styles.dropdownTitle}
          href={list.page_reference[0].url}
          locale={currentLocale}
          onClick={() => toggleDropdown(idx)}
        >
          <span>{list?.sub_navigation_see_all_button_label ? list.sub_navigation_see_all_button_label : `${header_strings?.see_all_label ? header_strings?.see_all_label : 'See All'} `}{!list.sub_navigation_see_all_button_label && <span dangerouslySetInnerHTML={{ __html: list.label }} />}
          </span>
        </Link>

        if (list?.sub_navigation_theme == 'list' || list?.sub_navigation_theme == 'grid')
          showParentLinkInDropdown = 'bottom';

        const numOfSubitems = list?.sub_navigation?.filter(sub => sub.page_reference?.[0]?.url).length ?? 0;
        const numOfColumns = list?.sub_navigation_grid_number_of_columns ?? 5;
        const subMenuStyle: Record<string, string | number> = {};

        subMenuStyle['--columns'] = numOfColumns;

        return (
        <div
          key={`${list.label}-${idx}`}
          className={`${styles.navbarLink}${list.sub_navigation_theme ? ` ${styles[`navbarLink--${list.sub_navigation_theme}`]}` : ` ${styles['navbarLink--default']}`}`}
          onMouseEnter={(e) => {
            if (!list.sub_navigation_theme) return;

            setOpenMenuIndex(idx);

            const item = e.currentTarget;

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const orientation = window.innerWidth-item.getBoundingClientRect().right > 400 ? 'left' : 'right';
                const dropdownRect = dropdownRef.current?.getBoundingClientRect();
                const dropdownMaxHeight = window.innerHeight - (dropdownRect?.top ?? 0);

                if (dropdownRef.current && dropdownMaxHeight <= (dropdownRect?.height ?? 0)) {
                  dropdownRef.current.style.setProperty('--max-height', `${dropdownMaxHeight}px`);
                  // dropdownRef.current.classList.add(styles['dropdown--scrollable']);
                  setOpenMenuScrollable(true);
                } else if (dropdownRef.current) {
                  dropdownRef.current.style.removeProperty('--max-height');
                  setOpenMenuScrollable(false);
                  // dropdownRef.current.classList.remove(styles['dropdown--scrollable']);
                }

                item.setAttribute('orientation', orientation);
              });
            });
          }}
          onMouseLeave={() => {
            if (!list.sub_navigation_theme) return;
            setOpenMenuIndex(null);
          }}
        >
          {list.page_reference?.[0]?.url && (
            <Link
              href={list.page_reference[0].url}
              locale={currentLocale}
              className={`${styles.mainLink} ${
                location === `${list.page_reference[0].url}` || openMenuIndex === idx
                  ? styles.active
                  : ''
              }`}
              onClick={e => {
                var clickAllowed = true;

                if (list?.sub_navigation_theme == 'list' || list?.sub_navigation_theme == 'grid')
                  clickAllowed = false;

                if (clickAllowed) {
                  const hasSubNav =
                    Array.isArray(list.sub_navigation) &&
                    list.sub_navigation.length > 0 &&
                    list.sub_navigation.every(sub => sub?.page_reference?.[0]?.url);

                  if (hasSubNav) {
                    e.preventDefault();
                    toggleDropdown(idx);
                  } else {
                    setOpenMenuIndex(null);
                  }
                }
              }}
            >
              {list.label}
            </Link>
          )}

          {/* Always render dropdown */}
          <div
            ref={dropdownRef}
            className={[
              `${styles.dropdown} ${openMenuIndex === idx ? styles.dropdownVisible : ''}`,
              `${openMenuScrollable ? styles['dropdown--scrollable'] : ''}`,
              list?.sub_navigation_callout?.page_reference?.[0]?.url && styles['dropdown--withCallout']
            ].filter(Boolean).join(' ')}
          >
            <div className={styles.megaMenu}>
              <div
                className={[
                  styles.innerMenuWrap,
                  list?.sub_navigation_callout && list?.sub_navigation_callout?.page_reference?.[0]?.url &&
                    styles['innerMenuWrap--withCallout']
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-rows={numOfSubitems ? Math.ceil(numOfSubitems / numOfColumns) : ''}
                style={subMenuStyle}
              >
                {showParentLinkInDropdown === 'top' && parentLinkInDropdown}
                <section className={styles.linkGroup}>
                  <div className={styles.subLinks}>
                    {Array.isArray(list.sub_navigation) &&
                      list.sub_navigation
                        .filter(sub => sub.page_reference?.[0]?.url)
                        .map((sub, sIdx) => (
                          <div
                            key={`${sub.label}-${sIdx}`}
                            className={styles.megaMenuL2}
                            style={{ gap: sub.title_image?.url ? 'var(--base)' : 'var(--xs)' }}
                          >
                            <Link
                              href={`${sub.page_reference[0].url}${
                                sub.querystring ? `?${sub.querystring}` : ''
                              }${getHash(sub.hash)}`}
                              locale={currentLocale}
                              className={styles.multiSlide}
                              onClick={() => toggleDropdown(idx)}
                            >
                              {sub.title_image?.url && (
                                <img
                                  className={styles.logoImageDropdown}
                                  src={getCachedAsset(sub.title_image.url, {width: 400, quality: 75})}
                                  alt={`${sub?.label} icon`}
                                />
                              )}
                              <span dangerouslySetInnerHTML={{ __html: sub.label }} />
                              {!list.sub_navigation_theme &&
                              <div className={styles.Menul2description}>
                                <span dangerouslySetInnerHTML={{ __html: sub?.description ?? '' }} />
                              </div>
                              }
                            </Link>
                          </div>
                        ))}
                    {getDropdownBanner(list)}
                  </div>
                </section>
                {showParentLinkInDropdown == 'bottom' && parentLinkInDropdown}
                {list.sub_navigation_theme && list?.sub_navigation_callout && list?.sub_navigation_callout?.page_reference?.[0]?.url && <>
                  <div className={styles.megaMenuCallout}>
                    <div className={styles.megaMenuCalloutWrapper}>
                      {list?.sub_navigation_callout?.title && <>
                      <div className={styles.megaMenuCalloutTitle} dangerouslySetInnerHTML={{ __html: list?.sub_navigation_callout?.title }} />
                      </>}
                      {list?.sub_navigation_callout?.image && <>
                      <div className={styles.megaMenuCalloutImage}>
                        <Link
                          href={list?.sub_navigation_callout?.page_reference?.[0]?.url}
                        >
                          <img src={getCachedAsset(list?.sub_navigation_callout?.image?.url, {width: 800, quality: 75})} alt="Mega Menu Callout Image" />
                        </Link>
                      </div>
                      </>}
                      {<Link
                        href={list?.sub_navigation_callout?.page_reference?.[0]?.url}
                      >{list?.sub_navigation_callout?.link_label ? list?.sub_navigation_callout?.link_label : 'Explore'}</Link>}
                    </div>
                  </div>
                </>}
              </div>
            </div>
          </div>


        </div>)
      })}

      {contact_us?.page_reference?.[0]?.url && (
        <div className={styles.navbarLink}>
          <Link
            href={`${isDefaultLocale ? '' : `/${locale}`}${contact_us.page_reference[0].url}`}
            className={styles.mainLink}
          >
            <span className={styles.locations}>{contact_us.label}</span>
          </Link>
        </div>
      )}

      {locations?.page_reference?.[0]?.url && (
        <div className={styles.navbarLink}>
          <Link
            href={`${isDefaultLocale ? '' : `/${locale}`}${locations.page_reference[0].url}`}
            className={styles.mainLink}
          >
            <span className={styles.locations}>{locations.label}</span>
          </Link>
        </div>
      )}
      {showLanguageSelector && (
        <LanguageSelector
          languages={languages}
          currentLocale={currentLocale || 'en-us'}
          onLocaleChange={onLocaleChange}
          className={styles.navbarLink}
          linkClassName={styles.mainLink}
        />
      )}
    </nav>
  );
}
